import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Star, Clock, Bike, Plus, ShoppingCart } from "lucide-react";
import { api, brl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function StorePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [tab, setTab] = useState(null);
  const { addItem, count, subtotal } = useCart();

  useEffect(() => {
    api.get(`/stores/${id}`).then(({ data }) => {
      setStore(data);
      const cats = [...new Set(data.menu.map((m) => m.category))];
      setTab(cats[0] || null);
    });
  }, [id]);

  if (!store) return <div className="p-6 text-sm text-gray-500">Carregando loja…</div>;

  const cats = [...new Set(store.menu.map((m) => m.category))];
  const items = store.menu.filter((m) => m.category === tab);

  return (
    <div data-testid="store-page" className="pb-28">
      <div className="relative h-44">
        <img src={store.banner_url} alt={store.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <button
          data-testid="store-back-button"
          onClick={() => navigate(-1)}
          className="absolute top-3 left-3 p-2 rounded-full bg-white/95 gf-press"
        >
          <ArrowLeft size={19} />
        </button>
      </div>

      <div className="px-4 -mt-8 relative">
        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
          <h1 className="font-display text-2xl font-extrabold text-gf-black" data-testid="store-name">
            {store.name}
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">{store.address}</p>
          <div className="flex items-center gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1 font-bold">
              <Star size={14} fill="#F5C518" stroke="none" /> {store.rating.toFixed(1).replace(".", ",")}
            </span>
            <span className="flex items-center gap-1 text-gray-600"><Clock size={13} /> {store.delivery_time}</span>
            <span className="flex items-center gap-1 text-gray-600"><Bike size={13} /> {brl(store.delivery_fee)}</span>
          </div>
          <p className="text-[11px] text-mage-green font-semibold mt-2">{store.category} · {store.opening_hours}</p>
        </div>
      </div>

      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-200 mt-4">
        <div className="flex gap-1 overflow-x-auto no-scrollbar px-4">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`menu-tab-${c}`}
              onClick={() => setTab(c)}
              className="px-3 py-3 text-sm font-bold shrink-0 relative"
              style={{ color: tab === c ? "#FF7A1A" : "#6B7280" }}
            >
              {c}
              {tab === c && <span className="absolute bottom-0 left-2 right-2 h-[3px] rounded-full bg-gf-orange" />}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 mt-4 grid gap-3 gf-stagger" data-testid="menu-items">
        {items.map((m) => (
          <div key={m.id} className="flex gap-3 p-3 rounded-2xl border border-gray-200 bg-white">
            <img src={m.image_url} alt={m.name} className="w-[76px] h-[76px] rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-gf-black">{m.name}</p>
              <p className="text-xs text-gray-500 line-clamp-2">{m.description}</p>
              <p className="font-display font-extrabold text-gf-orange mt-1">{brl(m.price)}</p>
            </div>
            <button
              data-testid={`add-item-${m.id}`}
              onClick={() => { addItem(store, m); toast.success(`${m.name} adicionado ao carrinho`); }}
              className="self-end w-9 h-9 rounded-xl gf-gradient text-white flex items-center justify-center gf-press shadow"
            >
              <Plus size={18} />
            </button>
          </div>
        ))}
      </div>

      {count > 0 && (
        <Link
          to="/carrinho"
          data-testid="view-cart-bar"
          className="fixed bottom-[76px] left-1/2 -translate-x-1/2 w-[calc(100%-32px)] max-w-[488px] z-40 gf-gradient text-white rounded-2xl px-4 py-3.5 flex items-center justify-between shadow-2xl gf-press"
        >
          <div>
            <p className="font-display font-extrabold text-sm">Ver carrinho</p>
            <p className="text-[11px] text-white/85">{count} {count === 1 ? "item" : "itens"} · {brl(subtotal)}</p>
          </div>
          <ShoppingCart size={20} />
        </Link>
      )}
    </div>
  );
}
